import type React from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { fmtHours, fmtInt, fmtPct } from "@/lib/format";
import type { TrendPoint } from "../../periods/usePeriods";
import type { Slide, SlideContext } from "../slide.types";
import { Tile } from "../visuals/Tile";
import { ACCENT_BLUE, ACCENT_ORANGE, ACCENT_RED } from "../colors";

const AXIS_TICK = { fontSize: 11, fill: "rgba(255,255,255,0.4)", fontFamily: "Inter" };
const TOOLTIP_STYLE = {
  fontFamily: "Inter",
  fontSize: 12,
  borderRadius: 8,
  background: "#1f1f1f",
  border: "1px solid rgba(255,255,255,0.1)",
  color: "#fff",
};

function pctDelta(latest: number, previous: number): number | null {
  if (previous === 0) return null;
  return ((latest - previous) / previous) * 100;
}

function deltaSub(latest: number, previous: number): string | undefined {
  const pct = pctDelta(latest, previous);
  if (pct == null) return undefined;
  return `${pct >= 0 ? "+" : ""}${pct.toFixed(1)}% vs previous period`;
}

interface TrendLineChartProps {
  title: string;
  dataKey: keyof Pick<
    TrendPoint,
    "extended_session_rate" | "productive_hours" | "fleet_idle_rate_pct"
  >;
  stroke: string;
  yUnit: "pct" | "hours";
  trends: TrendPoint[];
}

function TrendLineChart({
  title,
  dataKey,
  stroke,
  yUnit,
  trends,
}: TrendLineChartProps): React.JSX.Element {
  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
      <p className="text-white/40 text-[11px] font-body uppercase tracking-widest mb-3">{title}</p>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={trends} margin={{ left: 10, right: 20, top: 5, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
          <XAxis
            dataKey="label"
            tick={AXIS_TICK}
            axisLine={{ stroke: "rgba(255,255,255,0.15)" }}
            tickLine={false}
          />
          <YAxis
            tick={AXIS_TICK}
            axisLine={{ stroke: "rgba(255,255,255,0.15)" }}
            tickLine={false}
            tickFormatter={(v: number) =>
              yUnit === "pct" ? `${v.toFixed(0)}%` : `${v.toFixed(0)}h`
            }
          />
          <Tooltip
            contentStyle={TOOLTIP_STYLE}
            formatter={(v) => {
              const n = typeof v === "number" ? v : Number(v);
              return yUnit === "pct" ? fmtPct(n) : fmtHours(n);
            }}
          />
          <Line
            type="monotone"
            dataKey={dataKey}
            name={title}
            stroke={stroke}
            strokeWidth={2}
            dot={{ r: 3 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function MonthlyTrendsSlideView({ trends, resetKey }: SlideContext): React.JSX.Element {
  // buildSlides only includes this slide when trends.length > 1, so latest/
  // previous are always defined here — but noUncheckedIndexedAccess still
  // requires the narrowing.
  const latest = trends[trends.length - 1];
  const previous = trends[trends.length - 2];
  if (!latest || !previous) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-white/40 text-sm font-body">
          Not enough trend data for this client yet.
        </p>
      </div>
    );
  }

  const sessionsSub = deltaSub(latest.total_sessions, previous.total_sessions);
  const hoursSub = deltaSub(latest.productive_hours, previous.productive_hours);
  const extRateSub = deltaSub(latest.extended_session_rate, previous.extended_session_rate);
  const idleRateSub = deltaSub(latest.fleet_idle_rate_pct, previous.fleet_idle_rate_pct);

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <Tile
          label="Total Sessions"
          value={latest.total_sessions}
          format={(v) => fmtInt(Math.round(v))}
          delay={100}
          resetKey={resetKey}
          {...(sessionsSub !== undefined ? { sub: sessionsSub } : {})}
        />
        <Tile
          label="Productive Hours"
          value={latest.productive_hours}
          format={(v) => fmtHours(v)}
          delay={200}
          accent={ACCENT_BLUE}
          resetKey={resetKey}
          {...(hoursSub !== undefined ? { sub: hoursSub } : {})}
        />
        <Tile
          label="Extended Session Rate"
          value={latest.extended_session_rate}
          format={(v) => fmtPct(v)}
          delay={300}
          accent={ACCENT_ORANGE}
          resetKey={resetKey}
          {...(extRateSub !== undefined ? { sub: extRateSub } : {})}
        />
        <Tile
          label="Fleet Idle Rate"
          value={latest.fleet_idle_rate_pct}
          format={(v) => fmtPct(v)}
          delay={400}
          accent={ACCENT_RED}
          resetKey={resetKey}
          {...(idleRateSub !== undefined ? { sub: idleRateSub } : {})}
        />
      </div>

      <div className="space-y-4">
        <TrendLineChart
          title="Extended Session Rate Over Time"
          dataKey="extended_session_rate"
          stroke={ACCENT_ORANGE}
          yUnit="pct"
          trends={trends}
        />
        <TrendLineChart
          title="Productive Hours Over Time"
          dataKey="productive_hours"
          stroke={ACCENT_BLUE}
          yUnit="hours"
          trends={trends}
        />
        <TrendLineChart
          title="Fleet Idle Rate Over Time"
          dataKey="fleet_idle_rate_pct"
          stroke={ACCENT_RED}
          yUnit="pct"
          trends={trends}
        />
      </div>
    </>
  );
}

export const monthlyTrendsSlide: Slide = {
  id: "monthly-trends",
  title: "Monthly Trends",
  render: (ctx) => <MonthlyTrendsSlideView {...ctx} />,
};
